import { EventContentArg, EventInput, EventSourceFuncArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import momentPlugin from '@fullcalendar/moment';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
  ContentContainer,
  EllipsedText,
  FlexColumn,
  FlexRow,
  LabeledData,
  useDebouncedSearch,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Popover } from 'baseui/popover';
import { LabelSmall, ParagraphSmall } from 'baseui/typography';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useStyles } from '../../../hooks/useStyles';
import { getJobs } from '../../../services/jobsService';
import { jobHoverState, jobIsHoverState, jobsSearchFilterState } from '../../../storage/jobCalendarStates';
import { jobsFiltersState } from '../../../storage/jobStates';
import { CalendarJobExtendedProps } from '../../../types/jobs';
import { JobModal } from '../JobModal';
import { JobsCalendarFiltersBar } from './JobsCalendarFiltersBar';
import './jobsCalendarStyles.css';

export function JobsCalendarPage() {
  const { search, setSearch, trigger } = useDebouncedSearch(useRecoilState(jobsSearchFilterState));
  const filters = useRecoilValue(jobsFiltersState);
  const resetSearch = useResetRecoilState(jobsSearchFilterState);
  const resetFilters = useResetRecoilState(jobsFiltersState);
  const calendarRef = useRef<FullCalendar>(null);
  const { showModal, openModal, closeModal } = useModal();
  const [selectedJobId, setSelectedJobId] = useState<string>();
  const { isLarge } = useBreakpoints();
  const setJobHover = useSetRecoilState(jobHoverState);

  useEffect(() => {
    return () => {
      resetSearch();
      resetFilters();
    };
  }, []);

  const loadJobs = async (startDate: number, endDate: number): Promise<EventInput[]> => {
    const { paginatedResponse, error } = await getJobs(1, search, { ...filters, paginate: false, startDate, endDate });
    if (!error && paginatedResponse?.items) {
      let jobs = paginatedResponse.items;
      return jobs?.map((job) => {
        return {
          id: job._id,
          title: job.name,
          start: new Date(job.scheduleDate).toISOString(),
          jobDetail: job,
          source: jobs,
        };
      });
    } else {
      return [];
    }
  };

  const onJobDetail = (jobId: string) => {
    setSelectedJobId(jobId);
    openModal();
  };

  const getEvents = useCallback(
    (info: EventSourceFuncArg, successCallback: (eventInputs: EventInput[]) => void) => {
      loadJobs(info.start.getTime(), info.end.getTime()).then((events) => {
        successCallback(events);
      });
    },
    [trigger, filters]
  );

  const onCloseModal = () => {
    setSelectedJobId(undefined);
    closeModal();
  };

  return (
    <ContentContainer>
      <JobsCalendarFiltersBar
        search={search}
        onChangeSearch={setSearch}
      />
      <FullCalendar
        ref={calendarRef}
        plugins={[momentPlugin, dayGridPlugin, interactionPlugin, listPlugin, timeGridPlugin]}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: isLarge ? 'timeGridDay,list' : 'dayGridMonth,timeGridWeek,timeGridDay,list',
        }}
        initialView={isLarge ? 'timeGridDay' : 'dayGridMonth'}
        locales={[{ code: 'es' }]}
        buttonText={{
          day: 'Día',
          month: 'Mes',
          today: 'Hoy',
          week: 'Semana',
          list: 'Lista',
        }}
        dateClick={({ date }) => {
          // TODO: Add creation logic here if needed
        }}
        eventContent={(eventContent) => <CalendarEventContent eventContent={eventContent} />}
        eventDidMount={(mountArg) => {
          mountArg.el.style.height = 'min-content';
        }}
        eventMouseEnter={(hoverArgs) => {
          setJobHover(hoverArgs.event.id);
        }}
        eventMouseLeave={() => {
          setJobHover(undefined);
        }}
        eventClick={(arg) => {
          onJobDetail(arg.event.id);
        }}
        events={getEvents}
        noEventsText="No se encontraron trabajos para esta fecha."
        slotLabelFormat="h:mm a"
        listDayFormat="h:mm a"
        eventTimeFormat="h:mm a"
        allDayText="Todo el día"
      />
      {selectedJobId && (
        <JobModal
          jobId={selectedJobId}
          isOpen={showModal}
          onClose={onCloseModal}
        />
      )}
    </ContentContainer>
  );
}

const CalendarEventContent = memo(({ eventContent }: { eventContent: EventContentArg }) => {
  const { css, classes, theme } = useStyles();
  const { formatDateFromTs, formatTimeFromTs } = useMoment();
  const labelWidth = '75px';
  const viewType = eventContent.view.type;
  const withDot = ['dayGridMonth'].includes(viewType);
  const extendedProps = eventContent.event.extendedProps as CalendarJobExtendedProps;
  const { jobDetail } = extendedProps;
  const jobIsHover = useRecoilValue(jobIsHoverState(jobDetail._id));

  return (
    <Popover
      triggerType="hover"
      isOpen={jobIsHover}
      placement="topLeft"
      content={
        <FlexColumn
          gap={0}
          classNames={css({ padding: theme.sizing.scale500, minWidth: '300px', maxWidth: '350px' })}
        >
          <LabeledData
            label="Tipo:"
            content={jobDetail.jobType.name || '-'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Técnico:"
            content={jobDetail.user?.name || '-'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Fecha:"
            content={formatDateFromTs(jobDetail.scheduleDate)}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Hora:"
            content={formatTimeFromTs(jobDetail.scheduleDate)}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Contacto:"
            content={jobDetail.description || '-'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Dirección:"
            content={
              <div
                className={css({
                  margin: 0,
                  color: theme.colors.contentSecondary,
                  ...theme.typography.ParagraphSmall,
                })}
              >
                <EllipsedText maxLines={4}>{jobDetail.geocode || '-'}</EllipsedText>
              </div>
            }
            labelWidth={labelWidth}
          />
        </FlexColumn>
      }
      overrides={{
        Body: {
          style: { background: theme.colors.backgroundPrimary },
        },
        Inner: {
          style: { background: theme.colors.backgroundPrimary },
        },
      }}
    >
      <FlexColumn
        classNames={css({ overflow: 'hidden' })}
        gap={0}
      >
        <LabelSmall style={{ fontWeight: theme.typography.DisplayMedium.fontWeight }}>
          {jobDetail.jobType.name}
        </LabelSmall>
        {jobDetail.user && jobDetail.user.name.length > 0 && (
          <ParagraphSmall margin={0}>{jobDetail.user.name}</ParagraphSmall>
        )}
        <FlexRow
          gap={0}
          classNames={classes.centeredStart}
        >
          {withDot && <div className="fc-daygrid-event-dot" />}
          <ParagraphSmall margin={0}>{eventContent.timeText}</ParagraphSmall>
        </FlexRow>
      </FlexColumn>
    </Popover>
  );
});
